/* ============================================================
   androidRunner.js — Enjin post telefon (3 lapis + self-healing)
   ============================================================
   Jalankan resipi (androidRecipe) langkah demi langkah, pancar status tiap
   langkah untuk Stepper UI, dan JATUH ke Draft bila gagal (tak pernah tekan
   Post membuta). Dipakai DUA tempat:
     · Test (calibrating:true) — satu larian penuh, SELALU berakhir Draft,
       rakam "peta" telefon, tanda ready bila lulus hujung-ke-hujung.
     · Bulk post (calibrating:false) — guna peta tersimpan; mod 'post' terbit,
       mod 'draft' simpan draft.

   3 LAPIS cari sasaran tiap langkah (turutan):
     1. selector (dump UI) → percuma, laju (skrin diam, kongsi semua pelanggan).
     2. coord tersimpan (peta telefon dari Test) → skrin bergerak.
     3. vision (AI tengok screenshot) → self-healing; rakam koordinat baharu.
   Semua gagal → langkah ERROR.

   JARING DRAFT: gagal SELEPAS sampai skrin post-details (caption dan seterus)
   → cuba tekan "Drafts" (simpan apa yang ada). Gagal SEBELUM itu → berhenti,
   lapor (tiada apa diterbit, telefon tak rosak). ⛔ Mod 'post' hanya tekan
   Post pada larian yang BERSIH (tiada langkah healed gagal / tiada error).

   ⚠️ Belum diuji hardware untuk resipi penuh — selector dari ukuran 29 Okt;
   langkah skrin-bergerak + popup + tag produk perlu ditala atas telefon
   sebenar (Test). Primitif (tap/vision/dump/input) SUDAH terbukti jalan.
   ============================================================ */

import { executeAction, resolveSelector } from './androidExecutor';
import { aiPropose } from '../services/androidWorkflow';
import { recipeFor, finishGoal } from './androidRecipe';
import { getCoord, saveCoord, deleteCoord, markReady, ensureVersion } from './androidCalibration';
import { loadAdminDefault } from './androidDefault';
import { videoCardPalingBaru, sahkanPostDiProfil, linkShopeeSalah } from './androidPostCheck';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Bersihkan teks untuk ADB `input text` — ia tak boleh unicode (emoji, en/em-dash,
// smart quotes) + aksara shell/input berisiko → taip GAGAL SENYAP (caption kosong,
// insiden 29 Okt). Normalkan dash/quote, buang non-ASCII, tukar aksara berisiko
// jadi ruang, had 150 (caption Shopee).
function sanitizeForInput(t) {
  return String(t == null ? '' : t)
    .replace(/[‐-―]/g, '-')          // – — ‐ dll → -
    .replace(/[‘’ʼ]/g, "'")      // ' ' → '
    .replace(/[“”]/g, '"')            // " " → "
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^\x20-\x7E]/g, '')               // buang semua non-ASCII (emoji dll)
    .replace(/[&()<>|;*\\"'`$]/g, ' ')          // aksara berisiko input/shell → ruang
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 150);
}

// Teks caption tepat yang ditaip. Caption berakhir dengan HASHTAG tanpa ruang → Shopee
// papar senarai cadangan hashtag yang TUTUP skrin, dan BACK TAK dapat tutupnya bila 150
// PENUH (ukur user 30 Okt). Fix dalam TEKS (bukan Back): pastikan berakhir dengan RUANG.
// Kalau 150 penuh (tak muat ruang) → BUANG hashtag terakhir dulu untuk beri ruang,
// kemudian tambah ruang (user terima korban hashtag akhir).
function captionUntukTaip(raw) {
  let cap = sanitizeForInput(raw);
  if (/#[^\s#]+$/.test(cap)) {
    if (cap.length < 150) cap += ' ';
    else cap = cap.replace(/\s*#[^\s#]+\s*$/, '').replace(/\s+$/, '') + ' ';
  }
  return cap;
}

// Langkah skrin post-details — dari sini Draft boleh disimpan bila gagal.
const DETAILS_FROM = 'caption';

// Keyakinan minimum vision sebelum kita percaya cadangannya (elak tap rawak).
const VISION_MIN_CONF = 0.55;

// Panggil AI vision: screenshot → cadangan satu aksi (disemak server allowlist).
// Pulang {action,target:{x,y}|{kind:'selector'},confidence,needsHuman,reason} atau null.
async function askVision(controller, goal) {
  let shot;
  try { shot = await controller.screenshot(); } catch { return null; }
  const b64 = String(shot?.dataUrl || '').split(',')[1] || '';
  if (!b64) return null;
  let data;
  try {
    data = await aiPropose({
      imageBase64: b64,
      goal,
      // Nama medan ikut server (screenW/screenH). Dulu tersalah nama → AI nampak 0 x 0.
      context: { screenW: shot.width, screenH: shot.height },
    });
  } catch (e) {
    // Beza sebab sebenar (bukan senyap): tiada lesen / had harian / ralat lain.
    const m = String(e?.message || '');
    if (/LESEN_DIPERLUKAN/.test(m)) return { visionError: 'vision-perlu-lesen-aktif' };
    if (/HAD_VISION_HARIAN/.test(m)) return { visionError: 'vision-had-harian-habis' };
    return { visionError: 'vision-ralat:' + (e?.code || m || 'tak diketahui') };
  }
  const p = data?.proposal || data;
  if (!p || typeof p !== 'object') return null;
  return p;
}

// Cuba selesaikan + laksana satu sasaran guna 3 lapis. Pulang status:
//   { ok, healed, reason }  — healed=true bila terpaksa jatuh ke vision.
async function resolveAndTap(controller, { appId, serial, stepId, selector, goal, calibratable, adminPos, pending, visionOnly }) {
  const sw = controller?.info?.screenWidth || 720;
  const sh = controller?.info?.screenHeight || 1280;

  // Lapis A: selector (skrin diam sahaja — dump boleh gagal pada skrin bergerak).
  // Universal (kongsi semua phone versi Shopee sama), percuma, laju.
  if (selector && (selector.id || selector.text || selector.desc)) {
    try {
      const els = await controller.dumpUiElements();
      if (resolveSelector(selector, els)) {
        await executeAction(controller, { action: 'tap', target: { kind: 'selector', ...selector } }, els);
        return { ok: true, healed: false, reason: 'selector' };
      }
    } catch { /* skrin beranimasi → jatuh ke lapis seterusnya */ }
  }

  // Lapis B: koordinat SENDIRI user (peta telefon dia). MENANG atas default admin —
  // posisi dia terbukti jadi, jadi update default admin tak ganggu dia.
  const coord = visionOnly ? null : getCoord(appId, serial, stepId, sw, sh);
  if (coord) {
    try {
      await executeAction(controller, { action: 'tap', target: { x: coord.x, y: coord.y } }, []);
      return { ok: true, healed: false, reason: 'coord', at: coord };
    } catch { /* jatuh ke lapis seterusnya */ }
  }

  // Lapis C: DEFAULT admin (ratio → pixel ikut skrin user). Muat phone yang
  // serupa susunan dgn admin → zero vision. TIDAK disimpan sbg coord user (biar
  // tuning admin akan datang tetap sampai). Salah posisi → verify/vision tangkap.
  if (!visionOnly && adminPos && Number.isFinite(Number(adminPos.rx)) && Number.isFinite(Number(adminPos.ry))) {
    const dx = Math.round(adminPos.rx * sw), dy = Math.round(adminPos.ry * sh);
    if (dx >= 0 && dy >= 0 && dx <= sw && dy <= sh) {
      try {
        await executeAction(controller, { action: 'tap', target: { x: dx, y: dy } }, []);
        return { ok: true, healed: false, reason: 'admin-default', at: { x: dx, y: dy } };
      } catch { /* jatuh ke vision */ }
    }
  }

  // Lapis D: vision (self-healing). Rakam koordinat kalau calibratable.
  if (!goal) return { ok: false, healed: false, reason: 'no-target' };
  const p = await askVision(controller, goal);
  if (!p) return { ok: false, healed: false, reason: 'vision-no-answer' };
  if (p.visionError) return { ok: false, healed: false, reason: p.visionError };
  if (p.needsHuman) return { ok: false, healed: false, reason: 'needs-human' };
  if (typeof p.confidence === 'number' && p.confidence < VISION_MIN_CONF) {
    return { ok: false, healed: false, reason: `vision-low-conf(${p.confidence})` };
  }
  // Vision boleh jawab "wait" dengan sasaran 0,0 — itu BUKAN arahan tekan. Dulu robot
  // tetap tekan bucu skrin, tanda hijau, dan simpan 0,0 dalam peta (ukur 31 Okt).
  if (p.action && p.action !== 'tap') return { ok: false, healed: false, reason: `vision-bukan-tap(${p.action})` };
  // Tentukan KOORDINAT tap. Model kadang pulang koordinat {x,y}, kadang nama
  // butang (selector). Kalau nama → baca skrin (galeri/editor BOLEH dibaca) &
  // cari pusat butang (padanan tepat → longgar "includes", cth "Next (1)").
  const t = p.target || {};
  let tx = Number(t.x), ty = Number(t.y);
  if (!(Number.isFinite(tx) && Number.isFinite(ty)) && (t.kind === 'selector' || t.id || t.text || t.desc)) {
    let els = [];
    try { els = await controller.dumpUiElements(); } catch { els = []; }
    const r = resolveByTextLoose(t, els);
    if (r) { tx = r.cx; ty = r.cy; }
  }
  if (!(Number.isFinite(tx) && Number.isFinite(ty)) || tx <= 0 || ty <= 0) {
    return { ok: false, healed: false, reason: 'vision-no-coord' };
  }
  try {
    await controller.tap(tx, ty);
  } catch (e) {
    return { ok: false, healed: false, reason: 'exec-fail:' + (e?.message || e) };
  }
  // Tangguh simpan: peta hanya ditulis bila SELURUH larian lulus (runRecipe). Tap
  // vision yang tersasar dalam larian gagal tak boleh jadi kedudukan tetap.
  if (calibratable && pending) pending[stepId] = { x: tx, y: ty };
  return { ok: true, healed: true, reason: 'vision', at: { x: tx, y: ty } };
}

// Cari elemen dari selector model → pusat bounds. Cuba padanan TEPAT dulu
// (resolveSelector: id/text/desc sama), kemudian LONGGAR (text/desc mengandungi)
// — supaya "Next" model padan "Next (1)" pada skrin.
function resolveByTextLoose(target, els) {
  const exact = resolveSelector(target, els);
  if (exact) return exact;
  const needle = String(target.text || target.desc || '').trim().toLowerCase();
  if (!needle) return null;
  const m = (els || []).find((e) => {
    if (!e.bounds) return false;
    const et = String(e.text || '').toLowerCase().trim();
    const ed = String(e.desc || '').toLowerCase().trim();
    // et.includes(needle): "Next (1)" ⊇ "next". needle.includes(et): "next (1)" ⊇ "next".
    // Arah kedua dihadkan panjang ≥3 elak padanan tersilap aksara pendek.
    if (et && (et.includes(needle) || (et.length >= 3 && needle.includes(et)))) return true;
    if (ed && (ed.includes(needle) || (ed.length >= 3 && needle.includes(ed)))) return true;
    return false;
  });
  if (!m) return null;
  return { cx: (m.bounds.x1 + m.bounds.x2) / 2, cy: (m.bounds.y1 + m.bounds.y2) / 2, el: m };
}

// Cari anchor (resource-id/text) pada skrin semasa via dump.
//   true  = jumpa · false = tak jumpa · null = dump gagal (skrin beranimasi).
async function anchorFound(controller, anchor) {
  if (!anchor) return null;
  let els;
  try { els = await controller.dumpUiElements(); } catch { return null; }
  const hit = (els || []).some((e) => (
    (anchor.id && e.id === anchor.id)
    || (anchor.text && e.text === anchor.text)
    || (anchor.textIncludes && String(e.text || '').includes(anchor.textIncludes))
  ));
  return hit;
}

// PENGESAH satu langkah → 'pass' | 'fail' | 'unknown'.
//   anchorPresent: skrin sasaran MESTI boleh dibaca (cth editor). Tiada/anim =
//     'fail' (editor sepatutnya dumpable). anchorGone: dah tinggalkan skrin itu —
//     dump gagal (skrin baru beranimasi) dikira BERJAYA tinggalkan.
async function verifyStep(controller, s) {
  const v = s.verify;
  if (!v) return 'unknown';
  if (v.focusIncludes) return (await waitFocus(controller, v.focusIncludes, v.ms)) ? 'pass' : 'fail';
  if (v.anchorPresent) {
    // Skrin mungkin masih beralih: cuba baca beberapa kali sebelum kata gagal.
    for (let k = 0; k < 4; k++) {
      if ((await anchorFound(controller, v.anchorPresent)) === true) return 'pass';
      await delay(800);
    }
    return 'fail';
  }
  if (v.anchorGone) {
    const f = await anchorFound(controller, v.anchorGone);
    if (f === null) return 'pass';   // dump gagal → kemungkinan besar dah pindah skrin
    return f ? 'fail' : 'pass';       // masih jumpa = belum pindah
  }
  return 'unknown';
}

// Adakah app yang betul masih DI DEPAN skrin? true=ya/tak-pasti · false=app lain
// (ditutup/diminimumkan/bertukar). Tak dapat baca → true (jangan false-fail).
async function appInFront(controller, pkg) {
  let fg = '';
  try { fg = await controller.getForegroundPackage(); } catch { return true; }
  if (!fg) return true;        // tak dapat baca foreground → anggap OK
  return fg === pkg;
}

// Tetingkap fokus semasa ('' = tak dapat baca). Jalan walau skrin beranimasi.
async function focusNow(controller) {
  try { return await controller.getFocusedWindow(); } catch { return ''; }
}

// Tunggu tetingkap fokus mengandungi nama skrin (ukur 31 Okt: skrin post tak boleh
// dibaca, dan setiap cubaan baca makan ~15 saat — jadi gate guna fokus, bukan dump).
async function waitFocus(controller, name, ms = 6000) {
  for (const hingga = Date.now() + ms; Date.now() < hingga;) {
    if ((await focusNow(controller)).includes(name)) return true;
    await delay(500);
  }
  return false;
}

// BUKTI dah tinggalkan skrin post: fokus bukan lagi skrin post.
async function leftPostScreen(controller, postScreen) {
  await delay(2000);
  const f = await focusNow(controller);
  return !!f && !f.includes(postScreen);
}

// Ambil piksel skrin telefon (bukti untuk skrin yang tak boleh dibaca).
async function grabPixels(controller) {
  const shot = await controller.screenshot();
  const img = new Image();
  await new Promise((ok, no) => { img.onload = ok; img.onerror = no; img.src = shot.dataUrl; });
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(img, 0, 0);
  return { w: c.width, h: c.height, at: (x, y) => g.getImageData(Math.round(x), Math.round(y), 1, 1).data };
}

// Toggle ON = hijau (ukur: ON 9,198,47 · OFF 224,224,224). Bulatan putih bergerak,
// jadi semak beberapa titik sekitar kedudukan toggle.
function toggleOn(px, at) {
  return [-24, -12, 0, 12, 24].some((dx) => {
    const d = px.at(at.x + dx, at.y);
    return d[1] > 150 && d[0] < 90 && d[2] < 110;
  });
}

// Tab Live & Video: bar bawah hitam (feed tak boleh dibaca kerana video main). Tab lain
// bar bawah putih, dan popup malapkan skrin, jadi dua-dua bukan feed.
function diFeed(px) {
  const y = px.h * (1538 / 1640);
  let gelap = 0;
  let n = 0;
  for (let x = px.w * 0.04; x < px.w * 0.96; x += px.w * 0.03, n++) {
    const d = px.at(x, y);
    if (d[0] < 60 && d[1] < 60 && d[2] < 60) gelap++;
  }
  return gelap / n > 0.7;
}

// Bilangan piksel gelap (teks) dalam kawasan caption — naik = caption betul-betul masuk.
function captionInk(px) {
  let n = 0;
  for (let y = px.h * 0.09; y < px.h * 0.2; y += 3) {
    for (let x = px.w * 0.42; x < px.w * 0.95; x += 3) {
      const d = px.at(x, y);
      if (d[0] < 90 && d[1] < 90 && d[2] < 90) n++;
    }
  }
  return n;
}

// `input text` pulang sebelum telefon habis taip, dan skrin post tak boleh dibaca. Jadi
// tengok tulisan: siap bila kiraan piksel teks naik dari asal dan berhenti berubah 3 kali
// berturut. Ikut kelajuan phone sebenar, bukan masa tetap. false = tak siap dalam tempoh.
async function tungguTaipHabis(controller, inkAsal) {
  let lama = -1;
  let sama = 0;
  for (const hingga = Date.now() + 30000; Date.now() < hingga;) {
    await delay(400);
    const n = captionInk(await grabPixels(controller));
    sama = n > inkAsal + 40 && Math.abs(n - lama) <= 6 ? sama + 1 : 0;
    lama = n;
    if (sama >= 3) return true;
  }
  return false;
}

// Pancar screenshot telefon ke UI (mirror) — hanya bila pemanggil minta
// (cb.onScreen). Best-effort: gagal screenshot tak hentikan larian.
async function emitScreen(controller, cb) {
  if (!cb || typeof cb.onScreen !== 'function') return;
  try { const s = await controller.screenshot(); if (s?.dataUrl) cb.onScreen(s.dataUrl); } catch { /* biar */ }
}

/* ------------------------------------------------------------
   runRecipe — larian penuh satu video.
   controller : AndroidController (sudah connect + getDeviceInfo)
   cfg : { appId, serial, mode:'post'|'draft', calibrating, params }
         params = { caption, productLink, videoDest }
   cb  : { onStep({index,total,id,label,status,note}), onLog(msg,level) }
   Pulang { status:'done'|'draft_saved'|'failed', finishedAs, failedAt, reason, draftSaved, belumSiap }.
   'failed' + draftSaved:true = step gagal TAPI Draft terbukti tersimpan, TIDAK lengkap:
   belumSiap = isi yang belum masuk (jangan kira sama dengan Draft lengkap).
------------------------------------------------------------ */
export async function runRecipe(controller, cfg = {}, cb = {}) {
  // App skrin-boleh-dibaca (TikTok, 4 Nov) → robot sendiri (tiada kalibrasi/vision).
  // Bentuk hasil SAMA, jadi pemanggil (handleRunPhone / Test) tak perlu berubah.
  const r0 = recipeFor(cfg.appId || 'shopee');
  if (r0?.engine === 'dump' && typeof r0.run === 'function') return r0.run(controller, cfg, cb);
  const res = await runRecipeSteps(controller, cfg, cb);
  // Test gagal = telefon BUKAN ready (termasuk ready lama dari larian terdahulu).
  if (cfg.calibrating && cfg.serial && res.status === 'failed') markReady(cfg.appId || 'shopee', cfg.serial, false);
  return res;
}

async function runRecipeSteps(controller, cfg, cb) {
  const onStep = typeof cb.onStep === 'function' ? cb.onStep : () => {};
  const log = (msg, level = 'info') => cb.onLog?.(msg, level);
  const { appId = 'shopee', serial = '', calibrating = false, params = {} } = cfg;
  // Test SELALU berakhir Draft (tak pernah terbit masa kalibrasi).
  const mode = calibrating ? 'draft' : (cfg.mode === 'post' ? 'post' : 'draft');

  const recipe = recipeFor(appId);
  if (!recipe) return { status: 'failed', reason: 'no-recipe' };
  if (!controller?.connected) return { status: 'failed', reason: 'not-connected' };
  if (!controller.info) { try { await controller.getDeviceInfo(); } catch { /* biar */ } }
  // Lapis 1: default admin (selector di kod + ratio kedudukan admin). Read gagal
  // → fallback kod. Version default menentukan bila user kena re-calibrate.
  const adminDefault = await loadAdminDefault(appId);
  // Peta basi (VERSION default naik = flow berubah) → buang, belajar semula.
  if (serial && adminDefault.version && ensureVersion(appId, serial, adminDefault.version)) {
    log('Peta telefon lama dibuang (default dikemas kini) — robot belajar semula.', 'info');
  }

  const steps = recipe.steps;
  const total = steps.length;
  let reachedDetails = false;
  const pending = {}; // koordinat vision larian ini — ditulis hanya bila semua step lulus
  const stepCtx = { appId, serial, mode, params, pending, log, screens: recipe.screens, adminPositions: { ...recipe.measuredPositions, ...adminDefault.positions } };

  for (let i = 0; i < total; i++) {
    const s = steps[i];
    const label = s.labelMs || s.labelEn || s.id;
    if (s.id === DETAILS_FROM) reachedDetails = true;
    onStep({ index: i, total, id: s.id, label, status: 'running', note: '' });
    await emitScreen(controller, cb); // mirror: skrin sebelum langkah

    // PENGAWAL APP — langkah selain 'launch' mesti berlaku DALAM app betul.
    // App ditutup/bertukar (user atau sistem) → berhenti BERSIH, jangan tekan
    // membuta (elak hijau palsu merentas banyak langkah). Punca ujian 29 Okt.
    if (s.kind !== 'launch') {
      const front = await appInFront(controller, recipe.pkg);
      if (!front) {
        onStep({ index: i, total, id: s.id, label, status: 'error', note: 'Shopee tak di depan' });
        log('Shopee ditutup / bukan app di depan — robot berhenti selamat. Buka Shopee, pastikan ia di depan, dan Run semula.', 'err');
        return finishOnFailure(controller, { reachedDetails, failedAt: s.id, reason: 'app-not-foreground', cb, stepCtx });
      }
    }

    try {
      let r = await runStep(controller, s, stepCtx);

      // PENGESAH — hijau hanya kalau skrin betul-betul sampai tempat dijangka.
      // Gagal → cuba SEKALI lagi (self-heal) → masih gagal → MERAH (berhenti).
      // Ini halang "hijau palsu" (step hijau tapi skrin tak maju → caption/produk
      // atas skrin salah). Punca insiden 29 Okt.
      if (r.ok && s.verify) {
        await delay(900);
        let outcome = await verifyStep(controller, s);
        if (outcome === 'fail') {
          log(`"${label}": skrin tak maju macam dijangka — cuba sekali lagi…`, 'warn');
          deleteCoord(appId, serial, s.id); delete pending[s.id]; // kedudukan ni terbukti salah
          r = await runStep(controller, s, stepCtx);
          if (r.ok) { await delay(900); outcome = await verifyStep(controller, s); } else { outcome = 'fail'; }
          if (outcome === 'fail') {
            onStep({ index: i, total, id: s.id, label, status: 'error', note: 'skrin tak maju' });
            log(`Langkah "${label}" tak sahih — skrin tak sampai tempat dijangka.`, 'err');
            return finishOnFailure(controller, { reachedDetails, failedAt: s.id, reason: 'verify-failed', cb, stepCtx });
          }
          r.healed = true; // berjaya selepas cuba semula = self-healed
        }
      }

      if (!r.ok) {
        deleteCoord(appId, serial, s.id); // jangan ulang kedudukan yang gagal
        onStep({ index: i, total, id: s.id, label, status: 'error', note: r.reason });
        log(`Langkah "${label}" gagal: ${r.reason}`, 'err');
        return finishOnFailure(controller, { reachedDetails, failedAt: s.id, reason: r.reason, cb, stepCtx });
      }
      onStep({
        index: i, total, id: s.id, label,
        status: r.healed ? 'healed' : 'pass',
        note: r.healed ? 'AI adjusted' : '',
      });
    } catch (e) {
      onStep({ index: i, total, id: s.id, label, status: 'error', note: e?.message || String(e) });
      log(`Langkah "${label}" ralat: ${e?.message || e}`, 'err');
      return finishOnFailure(controller, { reachedDetails, failedAt: s.id, reason: e?.message || String(e), cb, stepCtx });
    }
    await emitScreen(controller, cb); // mirror: skrin selepas langkah
    await delay(s.kind === 'launch' ? 6000 : 1200); // lancar sejuk lebih lama
  }

  // Semua langkah lulus (termasuk finish) → baru peta boleh dipercayai.
  Object.entries(pending).forEach(([k, p]) => saveCoord(appId, serial, k, p.x, p.y, { model: controller?.info?.model }));
  if (calibrating && serial) {
    markReady(appId, serial, true, { model: controller?.info?.model });
    log('Telefon ditanda READY — peta tersimpan.', 'ok');
  }
  return { status: mode === 'post' ? 'done' : 'draft_saved', finishedAs: mode };
}

// Laksana satu langkah ikut kind-nya.
async function runStep(controller, s, ctx) {
  const { appId, serial, mode, params, adminPositions, pending, screens, log } = ctx;
  const base = {
    appId, serial, stepId: s.id, selector: s.selector, goal: s.goal, calibratable: s.calibratable,
    adminPos: (adminPositions && adminPositions[s.id]) || null, // Lapis 1 default admin (ratio)
    pending,
  };

  switch (s.kind) {
    case 'launch': {
      const recipe = recipeFor(appId);
      // GATE video tertukar: step Select video tick video pertama galeri, jadi video card
      // ini MESTI paling baru. Bukan → berhenti sebelum sentuh Shopee, jangan tag produk
      // card ini atas video card lain.
      if (params.videoDest && !(await videoCardPalingBaru(controller, params.videoDest))) {
        return { ok: false, healed: false, reason: 'video-bukan-paling-baru' };
      }
      // Mula bersih: page Import URL simpan kotak + senarai selagi app hidup (ukur 31 Okt),
      // dan baki senarai boleh tag produk lain. Akaun kekal log masuk.
      await controller._run(['am', 'force-stop', recipe.pkg], 'Close app');
      await delay(800);
      await controller.launchApp(recipe.pkg);
      if (!(await waitFocus(controller, screens.home, 20000))) return { ok: false, healed: false, reason: 'shopee-tak-terbuka' };
      return { ok: true, healed: false, reason: 'launch' };
    }
    case 'tap':
      return resolveAndTap(controller, base);

    case 'open_feed': {
      // SENTIASA tekan tab ini. Shopee kadang buka terus ke sini, tapi phone lain mungkin
      // tidak, jadi jangan bergantung padanya. Kedudukan dulu (feed tak boleh dibaca dan
      // cubaan baca makan ~11 saat); tak sampai → cari ikut nama tab.
      const diSana = async () => { await delay(1800); return diFeed(await grabPixels(controller)); };
      const r = await resolveAndTap(controller, { ...base, selector: null });
      if (!r.ok) return r;
      if (await diSana()) return r;
      const r2 = await resolveAndTap(controller, base);
      if (!r2.ok) return r2;
      if (await diSana()) return r2;
      return { ok: false, healed: r2.healed, reason: 'tab-video-tak-disahkan' };
    }

    case 'open_create': {
      // Popup Shopee Drama menutup feed dan bar bawah kekal hitam, jadi ia hanya kelihatan
      // bila + tak buka kamera. Tekan X-nya (bulatan putih tengah bawah), kemudian + semula.
      const r = await resolveAndTap(controller, base);
      if (!r.ok) return r;
      if (await waitFocus(controller, screens.camera, 4000)) return r;
      const sw = controller?.info?.screenWidth || 720;
      const sh = controller?.info?.screenHeight || 1280;
      const x = adminPositions.popup_x;
      await controller.tap(x.rx * sw, x.ry * sh);
      await delay(1500);
      return resolveAndTap(controller, base);
    }

    case 'pick_video': {
      // Tab All campur gambar: screenshot lebih baru boleh tertick. Tab Video + tile
      // pertama = video paling baru (gate di step launch). GATE: nombor tick "1".
      const sw = controller?.info?.screenWidth || 720;
      const baca = async () => (await controller.dumpUiElements()).filter((e) => e.bounds);
      const tengah = (e) => controller.tap((e.bounds.x1 + e.bounds.x2) / 2, (e.bounds.y1 + e.bounds.y2) / 2);
      const tab = (await baca()).find((e) => String(e.text || '').trim() === 'Video');
      if (!tab) return { ok: false, healed: false, reason: 'tab-video-galeri-tak-jumpa' };
      await tengah(tab);
      await delay(1500);
      const tick = (await baca())
        .filter((e) => /:id\/ll_check$/.test(e.id || '') && e.bounds.x1 >= 0 && e.bounds.x2 <= sw)
        .sort((a, b) => a.bounds.y1 - b.bounds.y1 || a.bounds.x1 - b.bounds.x1)[0];
      if (!tick) return { ok: false, healed: false, reason: 'tiada-video-dalam-galeri' };
      await tengah(tick);
      await delay(1000);
      const ditick = (await baca()).some((e) => /:id\/check$/.test(e.id || '') && String(e.text || '').trim() === '1');
      return ditick ? { ok: true, healed: false, reason: 'video-ditick' } : { ok: false, healed: false, reason: 'tick-video-tak-disahkan' };
    }

    case 'reveal_tap': {
      // Controls auto-sorok masa video main → ketuk video dulu, baru cari sasaran.
      const sw = controller?.info?.screenWidth || 720;
      const sh = controller?.info?.screenHeight || 1280;
      try { await controller.tap(sw / 2, sh / 2); await delay(600); } catch { /* biar */ }
      return resolveAndTap(controller, base);
    }

    case 'input_caption': {
      const cap = captionUntukTaip(params.caption);
      // Skrin post TAK BOLEH dibaca (ukur 31 Okt) → guna kedudukan + bukti piksel.
      const sw = controller?.info?.screenWidth || 720;
      const sh = controller?.info?.screenHeight || 1280;
      const sebelum = cap ? await grabPixels(controller) : null;
      const r = await resolveAndTap(controller, base);
      if (!r.ok) return r;
      await delay(900); // keyboard naik
      if (cap) { try { await controller.inputText(cap); } catch (e) { return { ok: false, healed: r.healed, reason: 'caption-type:' + (e?.message || e) }; } }
      if (cap) {
        const mula = Date.now();
        if (!(await tungguTaipHabis(controller, captionInk(sebelum)))) return { ok: false, healed: r.healed, reason: 'caption-taip-tak-selesai' };
        log(`Caption siap ditaip (${cap.length} aksara, ${Math.round((Date.now() - mula) / 1000)}s).`);
      }
      // Cara manual user: Back (hilang keyboard) → ketuk luar kawasan caption.
      try { await controller.back(); } catch { /* biar */ }
      await delay(800);
      await controller.tap(Math.round(sw / 2), Math.round(sh * 0.58));
      await delay(700);
      if (cap && captionInk(await grabPixels(controller)) <= captionInk(sebelum) + 40) {
        return { ok: false, healed: r.healed, reason: 'caption-tak-disahkan' };
      }
      return { ok: true, healed: r.healed, reason: 'caption' };
    }

    case 'tag_product': {
      // Laluan TERBUKTI 31 Okt: Add product → ikon link → Paste Link → Import → tick baris
      // PALING ATAS → Add. Produk datang terus dari link card, bukan padanan nama (cara
      // nama pernah tag produk kedai lain). `input text` paksa huruf pertama besar
      // ("Https") dan Shopee tolak, jadi link dimasukkan melalui clipboard telefon.
      // Import List (ukur 31 Okt): baris terbaru sentiasa di atas · import semula naik ke
      // atas tanpa berganda · KEKAL walau app ditutup paksa · kosong lepas Add · link
      // ditolak = kotak tetap kosong, senarai tak berubah, mesej toast tak boleh dibaca.
      // Jadi satu-satunya bukti import berjaya = baris atas BERTUKAR. Select All dilarang:
      // ia akan tag sekali baki larian lepas.
      const link = String(params.productLink || '').trim();
      const salah = linkShopeeSalah(link);
      if (salah) return { ok: false, healed: false, reason: salah };
      const dump = () => controller.dumpUiElements();
      const cari = async (pred) => (await dump()).find((e) => e.bounds && pred(e)) || null;
      const tekan = (e) => controller.tap((e.bounds.x1 + e.bounds.x2) / 2, (e.bounds.y1 + e.bounds.y2) / 2);
      const teks = (re) => (e) => re.test(String(e.text || '').trim());
      const tunggu = async (pred, ms = 6000) => {
        for (const hingga = Date.now() + ms; Date.now() < hingga;) {
          const f = await cari(pred);
          if (f) return f;
          await delay(600);
        }
        return null;
      };
      // 1. Buka Add Product. GATE fokus bertukar; salah tempat → buang + vision sekali.
      let open = await resolveAndTap(controller, base);
      if (!open.ok) return open;
      if (!(await waitFocus(controller, screens.addProduct))) {
        deleteCoord(appId, serial, s.id);
        open = await resolveAndTap(controller, { ...base, visionOnly: true });
        if (!open.ok) return open;
        if (!(await waitFocus(controller, screens.addProduct))) return { ok: false, healed: open.healed, reason: 'add-product-screen-tak-buka' };
      }
      const healed = open.healed;
      await delay(1500);
      try {
        // 2. Ikon link atas-kanan (tiada teks) → GATE page Import URL.
        const ikon = adminPositions.import_url;
        const sw = controller?.info?.screenWidth || 720;
        const sh = controller?.info?.screenHeight || 1280;
        await controller.tap(ikon.rx * sw, ikon.ry * sh);
        if (!(await tunggu(teks(/^import url$/i)))) return { ok: false, healed, reason: 'import-url-tak-buka' };
        // 3. Masukkan link huruf kecil ke clipboard: taip "x<link>" (hanya huruf pertama
        //    dibesarkan), pilih bahagian link sahaja, salin, kosongkan kotak.
        const kotak = await cari((e) => /edittext/i.test(e.cls || ''));
        if (!kotak) return { ok: false, healed, reason: 'kotak-url-tak-jumpa' };
        const senarai = async () => {
          const els = (await dump()).filter((e) => e.bounds);
          const mula = els.find(teks(/^import list$/i));
          const tamat = els.find(teks(/^select all$/i));
          if (!mula || !tamat) return null;
          const isi = els
            .filter((e) => e.bounds.y1 >= mula.bounds.y2 && e.bounds.y2 <= tamat.bounds.y1)
            .sort((a, b) => a.bounds.y1 - b.bounds.y1);
          if (isi.some(teks(/^no product$/i))) return { atas: '', bulatan: null };
          // Bulatan tick = petak kecil boleh-klik di kiri baris.
          const bulatan = isi.find((e) => e.clickable && e.bounds.x2 - e.bounds.x1 < sw * 0.1) || null;
          return { atas: String(isi.find((e) => String(e.text || '').trim())?.text || '').trim(), bulatan };
        };
        const awal = await senarai();
        if (!awal) return { ok: false, healed, reason: 'import-list-tak-jumpa' };
        await tekan(kotak);
        await delay(800);
        await controller._run(['input', 'keycombination', '113', '29'], 'Select all');
        await controller._run(['input', 'keyevent', '67'], 'Key delete');
        await delay(700);
        // Asingkan huruf pertama: kotak ini terlepas huruf yang masuk terus selepasnya
        // (ukur 31 Okt: "x…" sekali taip jadi "shopee.com.my/…").
        await controller.inputText('x');
        await delay(900);
        await controller.inputText(link);
        // GATE: `input text` pulang sebelum telefon habis taip. Tunggu kotak lengkap dan
        // bahagian link huruf kecil — kalau tidak, kekunci Home masuk tengah-tengah taip.
        const mahu = ('x' + link).toLowerCase();
        let ditaip = '';
        for (let k = 0; k < 15 && ditaip.toLowerCase() !== mahu; k++) {
          await delay(800);
          ditaip = String((await cari((e) => /edittext/i.test(e.cls || '')))?.text || '').trim();
        }
        if (ditaip.toLowerCase() !== mahu) return { ok: false, healed, reason: 'taip-tak-lengkap:' + ditaip.slice(0, 40) };
        if (ditaip.slice(1) !== link) return { ok: false, healed, reason: 'taip-huruf-berubah:' + ditaip.slice(0, 40) };
        const kekunci = (kod, label) => controller._run(['input', 'keyevent', kod], label);
        const gabung = (kod, label) => controller._run(['input', 'keycombination', ...kod], label);
        // Ctrl+Home / Ctrl+Shift+End: link panjang berbalut dua baris, Home biasa berhenti
        // di awal baris kedua (clipboard dapat separuh link).
        await gabung(['113', '122'], 'Go to start');
        await kekunci('22', 'Key right');
        await gabung(['113', '59', '123'], 'Select to end');
        await gabung(['113', '31'], 'Copy');
        await delay(400);
        await gabung(['113', '29'], 'Select all');
        await kekunci('67', 'Key delete');
        await delay(600);
        // 4. Paste Link → GATE kotak berisi TEPAT link huruf kecil.
        const paste = await tunggu(teks(/^paste link$/i));
        if (!paste) return { ok: false, healed, reason: 'paste-link-tak-jumpa' };
        await tekan(paste);
        await delay(1000);
        const isi = await cari((e) => /edittext/i.test(e.cls || ''));
        if (String(isi?.text || '').trim() !== link) return { ok: false, healed, reason: 'paste-tak-sama:' + String(isi?.text || '').slice(0, 40) };
        // 5. Import → GATE baris atas bertukar. Tak bertukar = link ditolak ATAU produk sama
        //    tertinggal di atas; dua-dua tak dapat dibezakan, jadi gagal, jangan teka.
        const imp = await tunggu(teks(/^import$/i));
        if (!imp) return { ok: false, healed, reason: 'import-tak-jumpa' };
        await tekan(imp);
        let baru = null;
        for (const hingga = Date.now() + 10000; Date.now() < hingga && !baru;) {
          const kini = await senarai();
          if (kini?.atas && kini.atas !== awal.atas) baru = kini;
          else await delay(800);
        }
        if (!baru) return { ok: false, healed, reason: awal.atas ? 'import-tak-disahkan' : 'import-ditolak' };
        if (!baru.bulatan) return { ok: false, healed, reason: 'tick-tak-jumpa' };
        // 6. Tick baris atas → GATE butang jadi "Add(1)" → Add → GATE balik ke skrin post.
        await tekan(baru.bulatan);
        await delay(800);
        const butangAdd = await tunggu(teks(/^add\s*\(1\)$/i));
        if (!butangAdd) return { ok: false, healed, reason: 'tick-tak-disahkan' };
        await tekan(butangAdd);
        if (!(await waitFocus(controller, screens.post, 8000))) return { ok: false, healed, reason: 'produk-tak-disahkan' };
      } catch (e) {
        return { ok: false, healed, reason: 'tag:' + (e?.message || e) };
      }
      await delay(800);
      return { ok: true, healed, reason: 'tagged' };
    }

    case 'toggle_ai': {
      // Skrin post tak boleh dibaca → GATE warna toggle (hijau = ON). Layout turun lepas
      // produk ditag, jadi kedudukan terukur ini untuk keadaan SELEPAS tag.
      // Shopee kadang dah hidupkan sendiri (ukur 31 Okt) — ketuk masa ON = jadi OFF.
      const sw = controller?.info?.screenWidth || 720;
      const sh = controller?.info?.screenHeight || 1280;
      const sini = getCoord(appId, serial, s.id, sw, sh)
        || (base.adminPos && { x: base.adminPos.rx * sw, y: base.adminPos.ry * sh });
      if (sini && toggleOn(await grabPixels(controller), sini)) return { ok: true, healed: false, reason: 'ai-label-sudah-on' };
      const r = await resolveAndTap(controller, base);
      if (!r.ok) return r;
      await delay(800);
      if (r.at && toggleOn(await grabPixels(controller), r.at)) return r;
      deleteCoord(appId, serial, s.id);
      delete pending[s.id];
      const r2 = await resolveAndTap(controller, { ...base, visionOnly: true });
      if (!r2.ok) return r2;
      await delay(800);
      if (r2.at && toggleOn(await grabPixels(controller), r2.at)) return r2;
      delete pending[s.id];
      return { ok: false, healed: r2.healed, reason: 'ai-label-tak-disahkan' };
    }

    case 'finish': {
      // Post dan Drafts WAJIB kunci peta berasingan (kedudukan Drafts tak boleh tertukar
      // jadi Post). GATE: fokus dah tinggalkan skrin post.
      const r = await resolveAndTap(controller, {
        ...base, stepId: `finish_${mode}`, goal: finishGoal(mode),
        adminPos: (adminPositions && adminPositions[`finish_${mode}`]) || null,
      });
      if (!r.ok) return r;
      if (!(await leftPostScreen(controller, screens.post))) return { ok: false, healed: r.healed, reason: 'finish-tak-disahkan' };
      if (mode !== 'post') return r;
      // Post: "tinggal skrin" belum bukti naik. Card seterusnya bermula dengan tutup paksa
      // app, jadi WAJIB tunggu post disahkan di profil dulu. Gagal = jangan Post semula.
      const semak = await sahkanPostDiProfil(controller, {
        caption: captionUntukTaip(params.caption), profil: adminPositions.profile_icon, screens, log,
      });
      if (!semak.ok) return { ok: false, healed: r.healed, reason: 'post-tak-disahkan:' + semak.sebab };
      if (!semak.produk) return { ok: false, healed: r.healed, reason: 'post-naik-tanpa-produk' };
      if (!semak.aiLabel) return { ok: false, healed: r.healed, reason: 'post-naik-tanpa-ai-label' };
      if (!semak.captionPenuh) return { ok: false, healed: r.healed, reason: 'post-naik-caption-terpotong' };
      return r;
    }

    default:
      return { ok: false, healed: false, reason: 'unknown-kind:' + s.kind };
  }
}

// Isi post mengikut susunan resipi: step gagal + semua selepasnya belum sempat dibuat.
const ISI_POST = [['caption', 'caption'], ['tag_product', 'produk'], ['ai_label', 'label AI']];
function belumSiapDari(failedAt) {
  const i = ISI_POST.findIndex(([id]) => id === failedAt);
  return i < 0 ? [] : ISI_POST.slice(i).map(([, nama]) => nama);
}

// Gagal tengah jalan → jaring Draft: balik ke skrin post, tekan Drafts, sahkan.
async function finishOnFailure(controller, { reachedDetails, failedAt, reason, cb, stepCtx }) {
  const log = (msg, level = 'info') => cb.onLog?.(msg, level);
  await emitScreen(controller, cb); // mirror: tunjuk skrin yang tersekat
  // Step akhir gagal sahkan: Post/Drafts mungkin dah ditekan. JANGAN tekan apa-apa lagi
  // (bahaya tekan di feed, atau Post berganda).
  if (failedAt === 'finish') {
    log('Robot tak dapat sahkan hasil tekan terakhir. Semak telefon sebelum Run semula.', 'err');
    return { status: 'failed', failedAt, reason, draftSaved: false };
  }
  if (!reachedDetails) {
    log('Gagal sebelum skrin post — tiada apa diterbit, telefon selamat.', 'warn');
    return { status: 'failed', failedAt, reason, draftSaved: false };
  }
  const { screens, appId, serial, adminPositions } = stepCtx;
  // Tersekat dalam page Add Product → Back sampai skrin post. Semak fokus dulu tiap
  // kali: Back pada skrin post sendiri boleh keluar dari flow.
  for (let k = 0; k < 3; k++) {
    if ((await focusNow(controller)).includes(screens.post)) break;
    try { await controller.back(); } catch { /* biar */ }
    await delay(1200);
  }
  if (!(await focusNow(controller)).includes(screens.post)) {
    log('Tak dapat balik ke skrin post — Draft TIDAK disimpan. Tiada apa diterbit.', 'err');
    return { status: 'failed', failedAt, reason, draftSaved: false };
  }
  log('Cuba simpan sebagai Draft (jaring keselamatan)…', 'warn');
  const r = await resolveAndTap(controller, {
    appId, serial, stepId: 'finish_draft', selector: null, goal: finishGoal('draft'),
    calibratable: false, adminPos: adminPositions.finish_draft,
  });
  if (r.ok && await leftPostScreen(controller, screens.post)) {
    await emitScreen(controller, cb); // mirror: skrin terakhir, bukan skrin masa gagal
    log('Step gagal, tapi Draft terbukti tersimpan — tiada apa diterbit.', 'warn');
    return { status: 'failed', finishedAs: 'draft', failedAt, reason, draftSaved: true, belumSiap: belumSiapDari(failedAt) };
  }
  log('Draft tak dapat disahkan — tiada apa diterbit. Semak telefon.', 'err');
  return { status: 'failed', failedAt, reason, draftSaved: false };
}
